/**
 * `/admin/:insee`: the Commune screen (`docs/ADMIN.md` §2) — "where do we
 * stand, what is happening right now, where do we go next": the dashboard's
 * KPIs, the live sessions map, door coverage by street and building-referent
 * coverage, each pulled from its own endpoint (`docs/ADMIN.md` §3).
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  getBuildingReferents,
  getCommuneCoverage,
  getCommuneDashboard,
  getSessionsMap,
} from '../../../api/admin';
import { getCommunes } from '../../../api/sessions';
import { Card, Skeleton } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { formatQuality, formatShare } from '../format';
import { SessionsMap } from '../components/SessionsMap';

import type { GeoPoint } from '../../../api/sessions';
import type { BuildingReferents, CommuneStreetCoverage, SessionMapEntry } from '../types';
import type { CommuneDashboard } from '../../../api/types';

interface PageData {
  dashboard: CommuneDashboard;
  coverage: CommuneStreetCoverage;
  sessions: SessionMapEntry[];
  referents: BuildingReferents;
  centroid: GeoPoint | null;
}

/**
 * Render the Commune screen.
 *
 * @returns The page element.
 */
export function CommunePage() {
  const { insee = '' } = useParams<{ insee: string }>();
  const [data, setData] = useState<PageData | undefined>(undefined);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(undefined);
    setLoadError(false);
    Promise.all([
      getCommuneDashboard(insee),
      getCommuneCoverage(insee),
      getSessionsMap(insee),
      getBuildingReferents(insee),
      getCommunes(),
    ])
      .then(([dashboard, coverage, sessions, referents, communes]) => {
        if (cancelled) return;
        const commune = communes.results.find((row) => row.insee_code === insee);
        setData({ dashboard, coverage, sessions, referents, centroid: commune?.centroid ?? null });
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [insee]);

  if (loadError) {
    return (
      <p role="alert" className="admin-error">
        {t('admin.commune.loadError')}
      </p>
    );
  }

  if (!data) {
    return (
      <div className="admin-page">
        <Skeleton height="4rem" />
        <Skeleton height="16rem" />
        <Skeleton height="12rem" />
      </div>
    );
  }

  const { dashboard, coverage, sessions, referents, centroid } = data;

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <h1>{t('admin.commune.title')}</h1>
        <p className="admin-lead">{t('admin.commune.lead')}</p>
      </div>

      <div className="admin-kpis">
        <div className="admin-kpi">
          <p className="admin-kpi__value">
            {t('admin.commune.kpi.doorsRecordedValue', {
              recorded: dashboard.coverage.recorded,
              doors: dashboard.coverage.doors,
            })}
          </p>
          <p className="admin-kpi__label">{t('admin.commune.kpi.doorsRecorded')}</p>
        </div>
        <div className="admin-kpi">
          <p className="admin-kpi__value">{dashboard.open_sessions.length}</p>
          <p className="admin-kpi__label">{t('admin.commune.kpi.openSessions')}</p>
        </div>
        <div className="admin-kpi">
          <p className="admin-kpi__value">
            {formatQuality(dashboard.average_quality, t('admin.commune.kpi.noQuality'))}
          </p>
          <p className="admin-kpi__label">{t('admin.commune.kpi.averageQuality')}</p>
        </div>
        <div
          className={dashboard.notes_to_review > 0 ? 'admin-kpi admin-kpi--alerte' : 'admin-kpi'}
        >
          <p className="admin-kpi__value">{dashboard.notes_to_review}</p>
          <p className="admin-kpi__label">
            {t('admin.commune.kpi.notesToReview')}
            {dashboard.notes_to_review > 0 ? ` · ${t('admin.notes.title')}` : ''}
          </p>
        </div>
        <div className="admin-kpi">
          <p className="admin-kpi__value">{dashboard.cases_without_email}</p>
          <p className="admin-kpi__label">{t('admin.commune.kpi.casesWithoutEmail')}</p>
        </div>
        <div className="admin-kpi">
          <p className="admin-kpi__value">{dashboard.followups.failed}</p>
          <p className="admin-kpi__label">{t('admin.commune.kpi.followupsFailed')}</p>
        </div>
      </div>

      <Card className="admin-block">
        <p className="admin-kicker">{t('admin.commune.map.kicker')}</p>
        <p className="admin-lead">{t('admin.commune.map.lead')}</p>
        {sessions.length === 0 ? (
          <p className="admin-muted">{t('admin.commune.map.empty')}</p>
        ) : (
          centroid && <SessionsMap center={centroid} sessions={sessions} />
        )}
      </Card>

      <Card className="admin-block">
        <p className="admin-kicker">{t('admin.commune.coverage.kicker')}</p>
        <p className="admin-lead">{t('admin.commune.coverage.lead')}</p>
        {coverage.by_street.length === 0 ? (
          <p className="admin-muted">{t('admin.commune.coverage.empty')}</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.commune.coverage.street')}</th>
                  <th scope="col">{t('admin.commune.coverage.doors')}</th>
                  <th scope="col">{t('admin.commune.coverage.recorded')}</th>
                  <th scope="col">{t('admin.commune.coverage.targets')}</th>
                  <th scope="col">{t('admin.commune.coverage.share')}</th>
                </tr>
              </thead>
              <tbody>
                {coverage.by_street.map((row) => (
                  <tr key={row.street}>
                    <td>{row.street}</td>
                    <td>{row.doors}</td>
                    <td>{row.recorded}</td>
                    <td>{row.canvass_targets}</td>
                    <td>{formatShare(row.share)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="admin-block">
        <p className="admin-kicker">{t('admin.commune.referents.kicker')}</p>
        <p className="admin-lead">
          {t('admin.commune.referents.summary', {
            withReferent: referents.with_referent,
            total: referents.buildings,
          })}
        </p>
        {referents.rows.length === 0 ? (
          <p className="admin-muted">{t('admin.commune.referents.empty')}</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.commune.referents.building')}</th>
                  <th scope="col">{t('admin.commune.referents.doors')}</th>
                  <th scope="col">{t('admin.commune.referents.households')}</th>
                  <th scope="col">{t('admin.commune.referents.status')}</th>
                </tr>
              </thead>
              <tbody>
                {referents.rows.map((row) => (
                  <tr key={row.address_id}>
                    <td>{row.label}</td>
                    <td>{row.doors}</td>
                    <td>{row.households}</td>
                    <td>
                      <span
                        className={
                          row.has_referent
                            ? 'admin-status admin-status--ok'
                            : 'admin-status admin-status--missing'
                        }
                      >
                        {row.has_referent
                          ? t('admin.commune.referents.has')
                          : t('admin.commune.referents.missing')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
